import React from "react";
import PropTypes from "prop-types";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import EditIcon from "@material-ui/icons/Edit";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "../../../../fhg/components/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useHistory, useLocation } from "react-router-dom";
import get from "lodash/get";
import { EDIT_ITEM, PLACEHOLDER_IMAGE } from "../../../../Constants";
import { getProfileThumbnail } from "../../../../fhg/utils/Utils";
import { getEquipmentDescription } from "../../EquipmentUtil";
import AssetDetail from "../AssetDetail";
import eventCapture from "../../../../eventList";
import { posthogCapture } from "../../../../PostHogFunction";
import { reSubmitItemToIms } from "../../../../Utils/SubmitUtil";

const useStyles = makeStyles(
    (theme) => ({
      backgroundStyle: {
        backgroundColor: theme.palette.environment.light.level0.base,
        padding: theme.spacing(0, 3, 2),
        overflow: "auto",
        height: "100%",
      },
      imageFrame: {
        paddingTop: theme.spacing(1),
      },
      image: {
        maxHeight: theme.spacing(10) + 2,
        maxWidth: theme.spacing(10) + 2,
        border: `1px solid ${theme.palette.divider} !important`,
        padding: `${theme.spacing(0.5)}px !important`,
        position: "relative",
      },
      descriptionStyle: {
        marginLeft: `${theme.spacing(1)}px !important`,
      },
      location: {
        marginTop: theme.spacing(1),
      },
      spinnerMargin: {
        marginLeft: theme.spacing(0.5),
      },
    }),
    { name: "DetailTab" }
);

function DetailTab({ evalItem, evaluation, showThumbnail, showEquipmentTitle, width, onClose }) {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();

  const [isExporting, setIsExporting] = React.useState(false);
  const [successAlertVisible, setSuccessAlertVisible] = React.useState(false);
  const [errorAlertVisible, setErrorAlertVisible] = React.useState(false);

  const image = React.useMemo(
      () => {
        if (!evalItem || !showThumbnail) return null;
        return getProfileThumbnail(evalItem);
      },
      [showThumbnail, evalItem]
  );

  if (!evalItem) {
    return (
        <Grid
            container
            className={classes.backgroundStyle}
            wrap={"nowrap"}
            direction={"column"}
            justifyContent="center"
            alignItems="center"
            style={{ minHeight: 200 }}
        >
          <Typography variant="body1" color="textSecondary">
            Loading item details...
          </Typography>
        </Grid>
    );
  }

  const onEdit = () => {
    posthogCapture(eventCapture.EDIT_ITEM);
    history.push(EDIT_ITEM.format({ itemId: get(evalItem, "item.item_id") }), {
      ...location.state,
      isEdit: true,
      backPath: location.pathname,
    });
  };

  const handleSubmitAssetToIMS = async () => {
    posthogCapture(eventCapture.RESUBMIT_ASSET);
    setIsExporting(true);
    const itemId = get(evalItem, "item_id", get(evalItem, "PK"));

    try {
      await reSubmitItemToIms(itemId);
      setSuccessAlertVisible(true);
    } catch (e) {
      setErrorAlertVisible(true);
    } finally {
      setIsExporting(false);
    }
  };

  return (
      <Grid
          container
          className={classes.backgroundStyle}
          wrap={"nowrap"}
          direction={"column"}
      >
        <Grid
            container
            direction={"row"}
            wrap={"nowrap"}
            justifyContent={"space-between"}
            style={{ flex: "0 0 auto", marginTop: 8 }}
        >
          <Button
              className={"secondary-minimal-button-small no-print"}
              variant={"text"}
              style={{ width: 110, height: 36, marginLeft: -8 }}
              onClick={onEdit}
              disableRipple={true}
              disabled={
                  !evaluation ||
                  evaluation.item_count <= 0 ||
                  get(evalItem, "item.item_id") === undefined
              }
          >
            <EditIcon style={{ marginRight: 4, fontSize: 18 }} />
            <Typography
                color="inherit"
                variant="button"
                id={"equipmentDetail.editAsset.button"}
            />
          </Button>
        </Grid>
        <Grid
            container
            direction={"row"}
            wrap={"nowrap"}
            justifyContent={"space-between"}
            style={{ flex: "0 0 auto", marginTop: 8 }}
        >
          <Button
              variant={"text"}
              className={"secondary-button"}
              onClick={handleSubmitAssetToIMS}
              style={{ height: 33, minWidth: 180 }}
          >
            <Typography
                color="inherit"
                variant="button"
                id={"equipmentDetail.resendAssetToIMS.button"}
            />
          </Button>
        </Grid>
        {showThumbnail && image && (
            <Grid
                container
                className={classes.imageFrame}
                direction={"row"}
                wrap={"nowrap"}
            >
              <img
                  src={image}
                  alt=""
                  className={classes.image}
                  onError={(e) => {
                    e.target.src = PLACEHOLDER_IMAGE;
                  }}
              />
              <Grid
                  container
                  item
                  direction={"column"}
                  className={classes.descriptionStyle}
                  spacing={1}
              >
                <Grid item>
                  <Typography className={"level-3-heading"}>
                    {getEquipmentDescription(get(evalItem, "item"), {})}
                  </Typography>
                </Grid>
                <Grid item className={classes.location}>
                  <Typography className={"subtle-text-small"} values={evalItem}>
                    {get(evalItem, "item.location")}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
        )}
        <div>
          <Snackbar
              open={successAlertVisible}
              autoHideDuration={10000}
              onClose={() => setSuccessAlertVisible(false)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity="success" sx={{ width: "100%" }}>
              Asset Successfully Resubmited to IMS!
            </Alert>
          </Snackbar>
          <Snackbar
              open={errorAlertVisible}
              autoHideDuration={10000}
              onClose={() => setErrorAlertVisible(false)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              Asset Resubmition Failed, kindly check your internet or refresh
              the page!
            </Alert>
          </Snackbar>
          {isExporting && (
              <CircularProgress
                  className={classes.spinnerMargin}
                  size={50}
                  thickness={5}
              />
          )}
        </div>
        <AssetDetail
            key={`estimates-${evalItem?.item_id}`}
            evalItem={evalItem}
            showEquipmentTitle={showEquipmentTitle}
            width={width}
            onClose={onClose}
            showLocation={false}
        />
      </Grid>
  );
}

DetailTab.propTypes = {
  evalItem: PropTypes.object,
  evaluation: PropTypes.object,
  showThumbnail: PropTypes.bool,
  showEquipmentTitle: PropTypes.bool,
  width: PropTypes.number,
  onClose: PropTypes.func,
};

DetailTab.defaultProps = {
  showThumbnail: true,
  showEquipmentTitle: true,
};

export default DetailTab;
